import { useMemo } from 'react'

import type { AnalyticsResponse } from '@tribeplatform/gql-client/types'

import type { GroupBy, ChartTimeFrame, ChartTypes } from '../types/index.js'

import { AreaChartRecharts } from './AreaChartRecharts.js'
import { BarChartRecharts } from './BarChartRecharts.js'
import { CumulativeTinyAreaChartRecharts } from './CumulativeTinyAreaChartRecharts.js'
import { LineChartRecharts } from './LineChartRecharts.js'
import { PieChartRecharts } from './PieChartRecharts.js'
import { TinyAreaChartRecharts } from './TinyAreaChartRecharts.js'

interface Props {
  id: string
  type: Exclude<ChartTypes, 'Score'>
  data: AnalyticsResponse
  timeFrame: ChartTimeFrame
  groupBy?: GroupBy[]
  indexBy: string[]
  labels: string[]
  ColorTheme: string[]
  syncId: string
}

export const DataVisualizer = ({
  id,
  type,
  data,
  timeFrame,
  indexBy,
  labels,
  groupBy,
  ColorTheme,
  syncId,
}: Props) => {
  const dataMem = useMemo(() => {
    return {
      chartData: data,
      timeFrame,
      indexBy,
      labels,
      groupBy,
      ColorTheme,
      syncId,
      id,
    }
    // we only want the chart to rerender when data changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  switch (type) {
    case 'Line':
      return <LineChartRecharts data={dataMem} />
    case 'Bar':
      return <BarChartRecharts data={dataMem} />
    case 'Area':
      return <AreaChartRecharts data={dataMem} />
    case 'TinyArea':
      return <TinyAreaChartRecharts data={dataMem} />
    case 'CumulativeTinyArea':
      return <CumulativeTinyAreaChartRecharts data={dataMem} />
    case 'Pie':
      return <PieChartRecharts data={dataMem} />
    default:
      // Code should never reach the default case
      // eslint-disable-next-line no-case-declarations
      const exhaustiveCheck: never = type
      return exhaustiveCheck
  }
}
